import { PaginatedQueryParams } from "@/store/types"
import PagedResponse from "./PagedResponse"

export interface AreaEventHistory {
    created: Date,
	groupCreated: Date,
    closed: Date,
	groupClosed: Date,
    type: string,
    user: string,
    details: string,
    priority: number,
    category: string,
    note: string,
    eventId: number
}

export interface AreaEventHistoryWithPagination extends PagedResponse {
    data: AreaEventHistory[]
}

export interface AreaEventHistoryPaginatedSearchParams extends PaginatedQueryParams {
    fromDate: Date,
    toDate: Date,
    groupId: number,
    eventHistoryTypeIds: number[],
    includeAutohandled: boolean,
    responseIds: number[],
    searchTerm: string | null
}

export enum AreaEventHistoryTypeIds {
    Event = 1,
    Masking = 2,
    OnTest = 3
}

export enum AreaEventHistoryTypeNames {
    Event = "Event",
    Masking = "Masking",
    OnTest = "OnTest"
}

export enum AreaEventHistoryColumnKey {
    CreatedAreaTime = "createdAreaTime",
    CreatedLocalTime = "createdLocalTime",
    ClosedAreaTime = "closedAreaTime",
    ClosedLocalTime = "closedLocalTime",
    Type = "type",
    User = "user",
    Details = "details",
    Priority = "priority",
    Category = "category",
    Note = "note",
    EventId = "eventId",
    AlarmExtraValue = "alarmExtraValue",
    EventTypeTitle = "eventTypeTitle",
}

export interface AreaEventHistoryPrefs {
    columnConfig: string | null;
    defaultAutohandled: boolean | null;
}

export const conditionalAreaEventHistoryColumns: Readonly<Partial<Record<AreaEventHistoryColumnKey, AreaEventHistoryColumnDef>>> = {
    [AreaEventHistoryColumnKey.AlarmExtraValue]: { key: AreaEventHistoryColumnKey.AlarmExtraValue, order: 85,
        label: "Alarm Extra Value", defaultVisible: true, sortable: true, isTermLabel: true },
    [AreaEventHistoryColumnKey.EventTypeTitle]: { key: AreaEventHistoryColumnKey.EventTypeTitle, order: 95,
        label: "Event Type Title", defaultVisible: true, sortable: true, isTermLabel: true },
};

export interface AreaEventHistoryColumnDef {
    /** The key for this column, which should match one of the AreaEventHistoryColumnKey enum values */
    key: AreaEventHistoryColumnKey;
    /** The label to display for this column in the table header */
    label: string;
    /** The order in which this column should appear relative to other columns. Lower numbers appear first. */
    order: number;
    /** Represents the previous, non-pref-controlled default visibility */
    defaultVisible: boolean;
    /** Whether this column is sortable */
    sortable: boolean;
    /** Whether this column should use a custom cell renderer */
    useCustomCell?: boolean;
    /** Whether this column represents a term label */
    isTermLabel?: boolean;
    /** Applied as a CSS class on cells in this column */
    style?: string;
}

export const areaEventHistoryDefaultColumns: ReadonlyArray<AreaEventHistoryColumnDef> = [
    {
        key: AreaEventHistoryColumnKey.CreatedAreaTime,
        label: "Created (Area Time)",
        order: 1,
        defaultVisible: true,
        sortable: true
    },
    {
        key: AreaEventHistoryColumnKey.CreatedLocalTime,
        label: "Created (Local)",
        order: 2,
        defaultVisible: false,
        sortable: false
    },
    {
        key: AreaEventHistoryColumnKey.ClosedAreaTime,
        label: "Closed (Area Time)",
        order: 3,
        defaultVisible: true,
        sortable: true
    },
    {
        key: AreaEventHistoryColumnKey.ClosedLocalTime,
        label: "Closed (Local)",
        order: 4,
        defaultVisible: false,
        sortable: false
    },
    {
        key: AreaEventHistoryColumnKey.Type,
        label: "Type",
        order: 5,
        defaultVisible: true,
        sortable: true,
        isTermLabel: true
    },
    {
        key: AreaEventHistoryColumnKey.User,
        label: "User",
        order: 6,
        defaultVisible: true,
        sortable: true,
        useCustomCell: true,
        isTermLabel: true
    },
    {
        key: AreaEventHistoryColumnKey.Details,
        label: "Details",
        order: 7,
        defaultVisible: true,
        sortable: true,
        isTermLabel: true,
        style: "max-width:10vw!important;"
    },
    {
        key: AreaEventHistoryColumnKey.Priority,
        label: "Priority",
        order: 8,
        defaultVisible: true,
        sortable: true,
        isTermLabel: true
    },
    {
        key: AreaEventHistoryColumnKey.Category,
        label: "Category",
        order: 9,
        defaultVisible: true,
        sortable: true,
        isTermLabel: true,
        style: "max-width:8vw!important;"
    },
    {
        key: AreaEventHistoryColumnKey.Note,
        label: "Note",
        order: 10,
        defaultVisible: true,
        sortable: true,
        useCustomCell: true,
        isTermLabel: true,
        style: "max-width:10vw!important;"
    },
    {
        key: AreaEventHistoryColumnKey.EventId,
        label: "Event Id",
        order: 11,
        defaultVisible: true,
        sortable: true,
        useCustomCell: true,
        isTermLabel: true
    }
] as const;

export interface AreaEventHistoryColumnPref {
    key: AreaEventHistoryColumnKey;
    visible: boolean;
}